import Storage from 'services/base/storage'

const storage = new Storage('gsg-btn-position')

const V2GuideSteps = [
  'get_to_know_calendly',
  'scheduling_setup_and_share',
  'automate_meeting_prep_followup',
  'team_success',
]

const V2GuideAdminSteps = [
  'get_to_know_calendly',
  'add_team_members',
  'team_success',
  'scheduling_setup_and_share',
  'automate_meeting_prep_followup',
]

export const getSectionCompletion = ({ steps } = {}) => {
  if (!steps) return null

  return (steps.filter(({ userGuideSteps }) => !!userGuideSteps?.length).length / steps.length || 0) * 100
}

export const getSectionCompletedCount = ({ steps = [] } = {}) => (
  steps.filter(({ userGuideSteps }) => userGuideSteps?.length).length || 0
)

export const getCompletionStatus = (sections, includeOptional = true) => {
  const state = { completed: 0, total: 0 }

  sections.forEach((section) => {
    switch (section.identifier) {
      case 'get_to_know_calendly':
        state.total += (section.steps?.length ?? 0)
        state.completed += getSectionCompletedCount(section)
        break
      default: {
        const nonVideoSteps = section.steps?.filter((step) => step.clickActionKind !== 'video') ?? []
        state.total += nonVideoSteps.length
        state.completed += getSectionCompletedCount({ steps: nonVideoSteps })
      }
    }
  })

  if (!includeOptional) {
    // team_success step is optional, deduct its steps from the total and completed
    const teamSuccessSection = sections.find((section) => section.identifier === 'team_success')

    if (!teamSuccessSection) {
      return state
    }
    const nonVideoSteps = teamSuccessSection.steps?.filter((step) => step.clickActionKind !== 'video') ?? []
    state.total -= nonVideoSteps.length
    state.completed -= getSectionCompletedCount({ steps: nonVideoSteps })
  }

  return state
}

export const getV2Steps = (isAdmin = false) => (isAdmin ? V2GuideAdminSteps : V2GuideSteps)

export const getV2Sections = (sections, isAdmin = false) => sections.filter((s) => getV2Steps(isAdmin).includes(s.identifier))

export const isUsingAdminGuideSteps = (orgRole, introOrgType) => {
  if (orgRole === 'user') return false
  if (orgRole === 'admin') return true

  return introOrgType === 'team'
}

export const adminGuideSectionOrdering = (sections) => {
  const ordered = []
  V2GuideAdminSteps.forEach((step) => {
    const orderedStep = sections.find((section) => section.identifier === step)
    if (orderedStep) ordered.push(orderedStep)
  })
  return ordered
}

// We can remove this once the experiment is over and the invite step has been removed from the team_success section
export const sanitizeAdminSectionSteps = (section, steps, isAdminFeatureEnabled) => {
  if (!isAdminFeatureEnabled || section.identifier !== 'team_success') return steps
  const sanitized = []
  steps.forEach((step) => {
    if (step.identifier !== 'invite_users_to_your_organization') {
      sanitized.push(step)
    }
  })
  return sanitized
}

export const getLocalPosition = () => {
  const x = storage.get('x')
  const y = storage.get('y')

  if (!x || !y) return {}

  return {
    x: x.indexOf('.') !== -1 ? parseFloat(x) : parseInt(x, 10),
    y: y.indexOf('.') !== -1 ? parseFloat(y) : parseInt(y, 10)
  }
}

export const setLocalPosition = ({ x, y }) => {
  const { x: oldX, y: oldY } = getLocalPosition()

  storage.set('x', x)
  storage.set('y', y)

  // Storage events only trigger on different tabs, we need to manually trigger the event for the same tab
  const xEvent = new StorageEvent('storage', {
    key: 'gsg-btn-position_x',
    newValue: x,
    oldValue: oldX
  })
  const yEvent = new StorageEvent('storage', {
    key: 'gsg-btn-position_y',
    newValue: y,
    oldValue: oldY
  })

  window.dispatchEvent(xEvent)
  window.dispatchEvent(yEvent)
}

export const clearLocalPosition = () => {
  const { x: oldX, y: oldY } = getLocalPosition()

  storage.remove('x')
  storage.remove('y')

  // Storage events only trigger on different tabs, we need to manually trigger the event for the same tab
  const xEvent = new StorageEvent('storage', {
    key: 'gsg-btn-position_x',
    newValue: null,
    oldValue: oldX
  })
  const yEvent = new StorageEvent('storage', {
    key: 'gsg-btn-position_y',
    newValue: null,
    oldValue: oldY
  })
  window.dispatchEvent(xEvent)
  window.dispatchEvent(yEvent)
}
